﻿import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Download, Upload } from "lucide-react"
import * as React from "react"

import { DataTable, type Column } from "@/components/data-table"
import { Badge, Button, PageHeader, Select } from "@/components/ui"
import {
  useImportBatches,
  useImportEntities,
  type ImportBatch,
} from "@/features/imports/api"
import { BATCH_STATUS_LABELS, BATCH_STATUS_TONES } from "@/features/imports/model"
import { TemplateDialog } from "@/features/imports/template-dialog"
import { UploadDialog } from "@/features/imports/upload-dialog"
import { PAGE_SIZE } from "@/lib/constants"
import { useSession } from "@/lib/session"
import { formatDateTime } from "@/lib/utils"

export const Route = createFileRoute("/imports/")({ component: ImportsScreen })

const STATUSES = ["staged", "applied", "cancelled", "reverted"] as const

function ImportsScreen() {
  const navigate = useNavigate()
  const { isAdministrator } = useSession()
  const [uploading, setUploading] = React.useState(false)
  const [templating, setTemplating] = React.useState(false)
  const [page, setPage] = React.useState(1)
  const [entity, setEntity] = React.useState("")
  const [status, setStatus] = React.useState("")

  const entities = useImportEntities()
  const query = useImportBatches(page, entity, status)

  function openBatch(id: number) {
    void navigate({ to: "/imports/$batchId", params: { batchId: String(id) } })
  }

  function filterBy(setter: (value: string) => void) {
    return (event: React.ChangeEvent<HTMLSelectElement>) => {
      setter(event.target.value)
      // A narrower set rarely has the page you were on.
      setPage(1)
    }
  }

  const labels = new Map(
    (entities.data ?? []).map((option) => [option.slug, option.label]),
  )

  // The template belongs beside the upload rather than inside it: it is what
  // somebody needs *before* they have a file, and often on a different day
  // from the one they come back to upload it on. Secondary, because importing
  // is what this screen is for.
  const actions = (
    <>
      <Button variant="secondary" onClick={() => setTemplating(true)}>
        <Download className="h-4 w-4" aria-hidden />
        Download template
      </Button>
      <Button onClick={() => setUploading(true)}>
        <Upload className="h-4 w-4" aria-hidden />
        Import a file
      </Button>
    </>
  )

  // No sortable columns and no search: the endpoint offers neither, and newest
  // first is the only order an import history is ever read in.
  const columns: Column<ImportBatch>[] = [
    {
      key: "filename",
      header: "File",
      primary: true,
      cell: (batch) => <span className="font-medium">{batch.filename}</span>,
    },
    {
      key: "entity",
      header: "Imported into",
      cell: (batch) => labels.get(batch.entity) ?? batch.entity,
    },
    {
      key: "status",
      header: "Status",
      cell: (batch) => (
        <Badge tone={BATCH_STATUS_TONES[batch.status] ?? "neutral"}>
          {BATCH_STATUS_LABELS[batch.status] ?? batch.status}
        </Badge>
      ),
    },
    {
      key: "counts",
      header: "Rows",
      hideOnMobile: true,
      cell: (batch) => <RowTally counts={batch.counts} />,
    },
    {
      key: "applied_at",
      header: "Applied",
      cell: (batch) =>
        batch.applied_at ? (
          formatDateTime(batch.applied_at)
        ) : (
          <span className="text-muted-foreground">Not applied</span>
        ),
    },
  ]

  return (
    <>
      <PageHeader
        eyebrow="Data"
        title="Imports"
        description="Load a file, read what it would change, then decide. Nothing is written until you apply it."
        actions={isAdministrator ? actions : undefined}
      />

      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        count={query.data?.count ?? 0}
        rowKey={(batch) => batch.id}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        isLoading={query.isLoading}
        isRefreshing={query.isFetching && !query.isPending}
        isError={query.isError}
        onRetry={() => void query.refetch()}
        onRowClick={(batch) => openBatch(batch.id)}
        toolbar={
          <div className="flex flex-wrap gap-2">
            <Select
              value={entity}
              onChange={filterBy(setEntity)}
              aria-label="Filter by what was imported"
              className="w-auto"
            >
              <option value="">Everything imported</option>
              {(entities.data ?? []).map((option) => (
                <option key={option.slug} value={option.slug}>
                  {option.label}
                </option>
              ))}
            </Select>
            <Select
              value={status}
              onChange={filterBy(setStatus)}
              aria-label="Filter by status"
              className="w-auto"
            >
              <option value="">Any status</option>
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {BATCH_STATUS_LABELS[value]}
                </option>
              ))}
            </Select>
          </div>
        }
        emptyTitle={entity || status ? "No imports match that filter" : "No imports yet"}
        emptyDescription={
          entity || status
            ? "Clear the filters to see the whole history."
            : "Every import is kept here with what it changed, so an applied file can be read back or undone later."
        }
        emptyAction={
          // The empty state is exactly where somebody has no file yet, so the
          // template is offered alongside the upload rather than only above.
          isAdministrator && !entity && !status ? actions : undefined
        }
      />

      {templating && <TemplateDialog onClose={() => setTemplating(false)} />}

      {uploading && (
        <UploadDialog
          onClose={() => setUploading(false)}
          onStaged={(batchId) => {
            setUploading(false)
            openBatch(batchId)
          }}
        />
      )}
    </>
  )
}

/** The outcome of a batch in one line, without making the reader open it. */
function RowTally({ counts }: { counts: Record<string, number> }) {
  const parts = [
    counts.create ? `${counts.create} new` : null,
    counts.update ? `${counts.update} updated` : null,
    counts.terminate ? `${counts.terminate} terminated` : null,
    counts.reject ? `${counts.reject} rejected` : null,
  ].filter(Boolean)

  if (parts.length === 0) {
    return <span className="text-muted-foreground">Nothing to change</span>
  }
  return <span className="text-sm">{parts.join(", ")}</span>
}
